import { useEffect, useRef, useState } from 'react';

import cs from 'classnames';

import { cn } from 'lib/helpers/utility';

import Popover from 'components/shared/Popovers/Popover/Popover';

import ColorButton from './components/ColorButton';
import ColorDot from './components/ColorDot';
import { recommendedColors } from './lib/getColors';

/**
 * @param {import('./lib/propTypes').ColorPickerProps}
 */

const ColorPicker = ({
  color = '#0BA5EC',
  onChange = () => {},
  label = 'Color',
  className,
  labelClassName,
  buttonClassName,
  panelClassName,
  disabled,
  required,
  isDotPicker = false,
  panelPosition = 'right'
}) => {
  const [selectedColor, setSelectedColor] = useState(color);
  const popoverRef = useRef(null);

  useEffect(() => {
    setSelectedColor(color);
  }, [color]);

  const handleChange = (color, closePopover) => {
    setSelectedColor(color);
    onChange(color);
    closePopover();
    popoverRef?.current?.buttonRef?.current?.blur();
  };

  const handleOpen = (e) => {
    e.stopPropagation();
    popoverRef?.current?.buttonRef?.current?.click();
    popoverRef?.current?.buttonRef?.current?.focus();
  };

  return (
    <div className={className}>
      {label && (
        <div className="flex min-h-[28px] items-center pb-1">
          <label
            className={cn(
              'text-sm font-500 first-letter:capitalize',
              disabled ? 'text-neutral-400' : 'text-neutral-800',
              labelClassName
            )}>
            {label}
            {required && <span className="text-danger-400">*</span>}
          </label>
        </div>
      )}
      <Popover
        isFixed
        position={panelPosition}
        ref={popoverRef}
        buttonClassName={cn(
          '!p-0 !min-w-0 !min-h-0 h-[41px]',
          !isDotPicker &&
            'border border-solid border-neutral-200 focus-within:border-primary-300 focus-within:!outline-[#CBF0FD] !outline !outline-2 !outline-transparent focus-within:!outline-2'
        )}
        panelClassName="!p-0 !rounded-[4px] border border-solid border-neutral-200 shadow-[0_0_16px_0_rgba(55,65,69,0.08)]"
        buttonTheme="transparent"
        dataQa="color-picker"
        disabled={disabled}
        icon={false}
        text={
          isDotPicker ? (
            <ColorDot
              selectedColor={selectedColor}
              disabled={disabled}
              buttonClassName={buttonClassName}
              onOpen={handleOpen}
            />
          ) : (
            <ColorButton
              selectedColor={selectedColor}
              disabled={disabled}
              buttonClassName={buttonClassName}
              onOpen={handleOpen}
            />
          )
        }>
        {({ closePopover }) => (
          <div className={cn('w-[280px] p-4 pt-2', panelClassName)}>
            <div className="pb-2 text-xs font-600 leading-4 text-neutral-900">Colors</div>
            <div className="grid grid-cols-2 gap-2">
              {recommendedColors.map((row, rowIndex) => (
                <div key={rowIndex} className="flex gap-2">
                  {row.map((recommendedColor, colIndex) => {
                    const isSelected =
                      selectedColor?.toUpperCase() === recommendedColor?.toUpperCase();
                    return (
                      <button
                        key={colIndex}
                        type="button"
                        className={cn(
                          'group flex size-[24px] items-center justify-center p-0',
                          isSelected ? 'cursor-default' : 'cursor-pointer'
                        )}
                        onClick={() => handleChange(recommendedColor, closePopover)}>
                        <span
                          className={cs(
                            'size-[18px] rounded-full duration-150 ease-in',
                            isSelected
                              ? `border-3 border-solid border-white outline outline-2 outline-[${recommendedColor}]`
                              : 'group-hover:scale-110'
                          )}
                          style={{ background: recommendedColor, outlineColor: recommendedColor }}
                        />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
};

export default ColorPicker;
